import type { Draft } from "@/prisma/generated/client";
import { RETAIL_DRAFT_TOTAL_STEPS } from "@/lib/cat/draft-step-indices";

type DraftVehicleLabel = {
  primaryLine: string;
  secondaryLine: string | null;
  modelLine: string | null;
};

export function formatDraftVehicleLabel(
  draft: Pick<
    Draft,
    | "commissionYear"
    | "commissionNumber"
    | "vehicleModelDescription"
    | "vinNumber"
  >,
): DraftVehicleLabel {
  const year = draft.commissionYear?.trim() ?? "";
  const number = draft.commissionNumber?.trim() ?? "";
  const primaryLine =
    year && number
      ? `${year} — ${number}`
      : year || number || "Véhicule non renseigné";

  const model = draft.vehicleModelDescription?.trim();
  const vin = draft.vinNumber?.trim();

  return {
    primaryLine,
    modelLine: model || null,
    secondaryLine: vin || null,
  };
}

export function formatDraftClientLabel(
  draft: Pick<
    Draft,
    | "clientFirstName"
    | "clientLastName"
    | "companyDenomination"
    | "directorSale"
    | "rentSale"
    | "exclusiveKind"
  >,
): string {
  if (draft.directorSale) {
    return "Vente dirigeant";
  }

  if (draft.rentSale) {
    return "RENT";
  }

  const company = draft.companyDenomination?.trim();
  if (company) {
    return company;
  }

  const firstName = draft.clientFirstName?.trim();
  const lastName = draft.clientLastName?.trim();
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(" ");
  }

  if (draft.exclusiveKind?.trim()) {
    return draft.exclusiveKind.trim();
  }

  return "Client non renseigné";
}

export function computeDraftExpiry(updatedAt: Date, ttlDays: number) {
  const expiryDate = new Date(updatedAt);
  expiryDate.setDate(expiryDate.getDate() + ttlDays);

  const now = new Date();
  const msRemaining = expiryDate.getTime() - now.getTime();
  const daysRemaining = Math.max(
    0,
    Math.ceil(msRemaining / (1000 * 60 * 60 * 24)),
  );

  return {
    expiryDate,
    daysRemaining,
    expiringSoon: daysRemaining <= 2,
  };
}

export function computeDraftProgress(draft: Pick<Draft, "currentStepIndex">): {
  completed: number;
  total: number;
} {
  const completed = Math.min(
    Math.max(draft.currentStepIndex, 1),
    RETAIL_DRAFT_TOTAL_STEPS,
  );
  return { completed, total: RETAIL_DRAFT_TOTAL_STEPS };
}
